"use client"

import { useEffect } from 'react'
import { AlertCircle, XCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { TESLA_REQUIREMENTS, AudioMetadata, estimateOptimizedSize } from '@/lib/audio-utils'
import { fmtDurationPrecise } from '@/lib/tls-format'
import { announce } from '@/lib/announce'
import { Tally } from '@/components/ui/tls/tally'
import { Chrome } from '@/components/ui/tls/chrome'
import { Mono } from '@/components/ui/tls/mono'
import { cardVariants } from '@/components/ui/card'

interface CompatibilityIndicatorProps {
  metadata: AudioMetadata | null
  trimDuration: number
  isProcessing?: boolean
  className?: string
}

type CompatibilityStatus = 'ready' | 'fixable' | 'action-required'

interface StatusItem {
  label: string
  value: string
  status: 'ok' | 'warning' | 'error'
}

// §19.8: three verdict states. The verdict is carried by the word, the marks
// and whether an export action exists — never by colour; the system has no
// semantic green/amber/red. Sentence case, like every other sentence on the
// site: "Ready for the car." is §19.8's own announcement string.
const VERDICTS: Record<CompatibilityStatus, { tallyOn: boolean; label: string; labelColor: string }> = {
  ready: {
    tallyOn: true,
    label: 'Ready for the car',
    labelColor: 'var(--tls-brand-ink)',
  },
  fixable: {
    tallyOn: false,
    label: 'Will be converted',
    labelColor: 'var(--tlsr-fg-strong,var(--tls-fg-strong))',
  },
  'action-required': {
    tallyOn: false,
    label: 'Action required',
    labelColor: 'var(--tlsr-fg-strong,var(--tls-fg-strong))',
  },
}

function buildItems(metadata: AudioMetadata, trimDuration: number): StatusItem[] {
  const items: StatusItem[] = []

  const durationOk = trimDuration <= TESLA_REQUIREMENTS.MAX_DURATION
  items.push({
    label: 'Duration',
    value: fmtDurationPrecise(trimDuration),
    status: durationOk ? 'ok' : 'error',
  })

  const sampleRateOk = metadata.sampleRate === TESLA_REQUIREMENTS.SAMPLE_RATE
  items.push({
    label: 'Sample rate (playback quality)',
    value: `${(metadata.sampleRate / 1000).toFixed(1)} kHz`,
    status: sampleRateOk ? 'ok' : 'warning',
  })

  const channelsOk = metadata.numberOfChannels === TESLA_REQUIREMENTS.CHANNELS
  items.push({
    label: 'Channels',
    value: metadata.numberOfChannels === 1 ? 'Mono' : metadata.numberOfChannels === 2 ? 'Stereo' : `${metadata.numberOfChannels}ch`,
    status: channelsOk ? 'ok' : 'warning',
  })

  const estimatedSize = estimateOptimizedSize({ ...metadata, duration: trimDuration }, trimDuration)
  const sizeOk = estimatedSize <= TESLA_REQUIREMENTS.MAX_FILE_SIZE
  items.push({
    label: 'Est. size',
    value: `${(estimatedSize / 1024).toFixed(0)} KB`,
    status: sizeOk ? 'ok' : 'error',
  })

  return items
}

export function CompatibilityIndicator({
  metadata,
  trimDuration,
  isProcessing = false,
  className,
}: CompatibilityIndicatorProps) {
  const items = metadata ? buildItems(metadata, trimDuration) : []
  const hasErrors = items.some(item => item.status === 'error')
  const hasWarnings = items.some(item => item.status === 'warning')
  const durationOk = trimDuration <= TESLA_REQUIREMENTS.MAX_DURATION

  const overallStatus: CompatibilityStatus = hasErrors
    ? 'action-required'
    : hasWarnings
      ? 'fixable'
      : 'ready'

  const description =
    overallStatus === 'ready'
      ? 'Meets every Tesla requirement.'
      : overallStatus === 'fixable'
        ? 'The format is converted to the Tesla spec on export.'
        : !durationOk
          ? `Trim your selection to ${TESLA_REQUIREMENTS.MAX_DURATION} seconds or less.`
          : 'Fix the items marked below.'

  const verdict = VERDICTS[overallStatus]
  const verdictLine = metadata ? `${verdict.label}. ${description}` : null

  // §19.8 semantics, and where they actually live. The spec writes
  // role=status aria-live=polite on this panel, announcing only the verdict
  // line, throttled to 500 ms. §11.1 makes lib/announce.ts the site's single
  // live-region writer — the shell nodes #ls-live-polite / #ls-live-assertive
  // mount once in app/layout.tsx — so this panel carries no live attribute of
  // its own: the verdict line is routed through announce() into the shared
  // polite node whenever it changes, with §19.8's 500 ms throttle. A drag that
  // never crosses a verdict boundary changes nothing here and so announces
  // nothing, which is the "not a paragraph per frame" clause.
  useEffect(() => {
    if (verdictLine) announce(verdictLine, 'polite', 500)
  }, [verdictLine])

  if (!metadata) {
    return (
      <div className={cn("p-4", className)}>
        <div className="flex items-center gap-3 text-[var(--tlsr-fg-muted)]">
          <Chrome>Upload an audio file to check compatibility</Chrome>
        </div>
      </div>
    )
  }

  return (
    <div className={cn(cardVariants({ surface: "refresh" }), "p-5", className)}>
      {/* Verdict row: Tally, the verdict word at the §19.8 type-h4 step
          (sentence case: uppercase is reserved for the 11px label token),
          then the one-line consequence. */}
      <div className="mb-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <Tally on={verdict.tallyOn} size={8} />
        <p
          className="font-bold leading-[1.3] tracking-[-0.015em]"
          // type-h4 at the 16px reference root (19px), the same inline-rem
          // idiom the filter sheet's title uses for this step.
          style={{ color: verdict.labelColor, fontSize: "1.1875rem" }}
        >
          {verdict.label}
        </p>
        <span className="ml-auto text-xs text-[var(--tlsr-fg-muted)]">{description}</span>
      </div>

      {/* Spec checklist — one fact per line, hairline-separated, no per-line card (§19.8) */}
      <ul
        className="list-none p-0"
        aria-label="Tesla compatibility checklist"
      >
        {items.map((item) => {
          const isSatisfied = item.status === 'ok'
          return (
            <li
              key={item.label}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-[color:var(--tlsr-line)] py-2.5 last:border-b-0"
              data-satisfied={isSatisfied}
            >
              <Chrome style={{ minWidth: 0 }}>{item.label}</Chrome>
              <Mono
                size={12}
                color="var(--tls-fg-strong)"
                style={{ marginLeft: 'auto' }}
              >
                {item.value}
              </Mono>
              {/* Status indicator — shape carries the verdict, never colour (§19.8) */}
              {isSatisfied ? (
                <span
                  className="tlsr-tally-dot flex-shrink-0"
                  aria-label="Requirement met"
                />
              ) : item.status === 'warning' ? (
                <AlertCircle
                  className="size-3.5 flex-shrink-0 text-[var(--tlsr-fg-muted)]"
                  aria-label="Will be converted on export"
                />
              ) : (
                <XCircle
                  className="size-3.5 flex-shrink-0 text-[var(--tlsr-fg-strong,var(--tls-fg-strong))]"
                  aria-label="Requirement not met"
                />
              )}
            </li>
          )
        })}
      </ul>

      {/* Processing indicator — flat status line, no bordered box on content (Rule 1) */}
      {isProcessing && (
        <div className="mt-4 flex items-center gap-2 border-t border-[color:var(--tlsr-line)] pt-3 text-[var(--tls-brand-ink)]">
          <div
            className="size-4 flex-shrink-0 rounded-[var(--tls-radius-pill)] border-2 border-[var(--tls-brand-ink)] border-t-transparent animate-spin"
            aria-hidden="true"
          />
          <span className="text-sm">Processing audio…</span>
        </div>
      )}
    </div>
  )
}
